// WorkspaceUploadPolicyService.js

// Firebase
import { doc, getDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Services
import { getPlanByKey } from 'services/plans/PlanService';

const workspaceUploadPolicyCache = new Map();

function normalizePositiveInteger(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  const whole = Math.floor(parsed);
  if (whole < 1) return null;

  return whole;
}

function formatBytes(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return '0 B';
  }

  if (parsed < 1024) {
    return `${Math.floor(parsed)} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = parsed;
  let unitIndex = -1;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[Math.max(unitIndex, 0)]}`;
}

async function readWorkspaceUploadPolicy({ workspaceKey }) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  if (!cleanedWorkspaceKey) {
    return {
      maxUploadBytes: null,
      planKey: null,
      workspaceKey: null,
    };
  }

  const workspaceSnap = await getDoc(doc(db, collections.workspaces, cleanedWorkspaceKey));
  if (!workspaceSnap.exists()) {
    return {
      maxUploadBytes: null,
      planKey: null,
      workspaceKey: cleanedWorkspaceKey,
    };
  }

  const workspace = workspaceSnap.data() || {};
  const planKey = String(workspace.planKey || '').trim();
  if (!planKey) {
    return {
      maxUploadBytes: null,
      planKey: null,
      workspaceKey: cleanedWorkspaceKey,
    };
  }

  const plan = await getPlanByKey({ planKey }).catch(() => null);

  return {
    maxUploadBytes: normalizePositiveInteger(plan?.maxUploadBytes),
    planKey,
    workspaceKey: cleanedWorkspaceKey,
  };
}

export async function getWorkspaceUploadPolicy({ workspaceKey }) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  if (!cleanedWorkspaceKey) {
    return {
      maxUploadBytes: null,
      planKey: null,
      workspaceKey: null,
    };
  }

  if (!workspaceUploadPolicyCache.has(cleanedWorkspaceKey)) {
    const policyPromise = readWorkspaceUploadPolicy({ workspaceKey: cleanedWorkspaceKey })
      .catch((error) => {
        workspaceUploadPolicyCache.delete(cleanedWorkspaceKey);
        throw error;
      });

    workspaceUploadPolicyCache.set(cleanedWorkspaceKey, policyPromise);
  }

  return workspaceUploadPolicyCache.get(cleanedWorkspaceKey);
}

export async function assertWorkspaceUploadAllowed({ workspaceKey, fileName, fileSizeBytes }) {
  const normalizedSize = normalizePositiveInteger(fileSizeBytes);
  if (!normalizedSize) return;

  const { maxUploadBytes } = await getWorkspaceUploadPolicy({ workspaceKey });
  if (!maxUploadBytes || normalizedSize <= maxUploadBytes) {
    return;
  }

  const normalizedFileName = String(fileName || '').trim();
  const label = normalizedFileName ? `"${normalizedFileName}"` : 'This file';
  throw new Error(`${label} exceeds this space plan upload limit (${formatBytes(maxUploadBytes)}).`);
}